import { SERVER_TICK_MS } from '../../shared/constants';
import { CommandType, type MoveCommand, type PlayerCommand } from '../../shared/types';
import type { ClientNetManager } from './ClientNetManager';

export class InputSender {
    private net: ClientNetManager;
    private lastMoveTime: number = 0;
    private pendingMove: MoveCommand | null = null;
    private throttleMs: number;
    /**
     * Speed of the last move command actually sent. -1 = "nothing sent yet";
     * used to detect start/stop motion edges so the first key-down and
     * final key-up bypass the 50ms throttle and feel snappy.
     */
    private lastSentSpeed: number = -1;

    constructor(net: ClientNetManager, throttleMs: number = SERVER_TICK_MS) {
        this.net = net;
        this.throttleMs = throttleMs;
    }

    queueCommand(command: PlayerCommand): void {
        if (command.type !== CommandType.Move) {
            this.net.sendCommand(command);
            return;
        }
        this.pendingMove = command;
        // Start/stop edges bypass the throttle so input lands within one
        // frame instead of waiting up to throttleMs. Sustained motion and
        // mid-motion direction changes still throttle — small jitter on a
        // 20Hz server is fine, but the start/stop moments are perceptually
        // very sensitive.
        const isStartEdge = command.speed > 0 && this.lastSentSpeed <= 0;
        const isStopEdge = command.speed === 0 && this.lastSentSpeed > 0;
        if (isStartEdge || isStopEdge) {
            this.net.sendCommand(command);
            this.lastSentSpeed = command.speed;
            this.pendingMove = null;
            this.lastMoveTime = performance.now();
        }
    }

    flush(): void {
        const now = performance.now();
        if (this.pendingMove && now - this.lastMoveTime >= this.throttleMs) {
            this.net.sendCommand(this.pendingMove);
            this.lastSentSpeed = this.pendingMove.speed;
            this.pendingMove = null;
            this.lastMoveTime = now;
        }
    }
}
